import { Settings } from "./settings";

/**
 * 3 Orbs of Zeus — Full Symbol Data
 *
 * API symbol indices are 0-indexed (see docs/api-contract-3-orbs-of-zeus.md)
 *
 * Base game: 0 (wild), 1 (high pay), 2-4 (mid pay), 5-9 (low pay)
 * Feature:   10 (r0 placeholder), 11 (c0 scatter), 12-14 (zeus orbs), 15 (coin), 16 (blank)
 * Jackpots:  17-19 (mini/minor/major)
 * Variants:  20-25 (feature-specific coin variants)
 */
export const symbolData = {
  // ── Base game symbols ──
  0: { name: "w0", spine: "w0", symbolPay: "wild", texture: "w0", value: null },
  1: { name: "m1", spine: "mp", skin: "m1", symbolPay: "mp", texture: "m1", value: null },
  2: { name: "m2", spine: "mp", skin: "m2", symbolPay: "mp", texture: "m2", value: null },
  3: { name: "m3", spine: "mp", skin: "m3", symbolPay: "mp", texture: "m3", value: null },
  4: { name: "m4", spine: "mp", skin: "m4", symbolPay: "mp", texture: "m4", value: null },
  5: { name: "l1", spine: "lp", skin: "l1", symbolPay: "lp", texture: "l1", value: null },
  6: { name: "l2", spine: "lp", skin: "l2", symbolPay: "lp", texture: "l2", value: null },
  7: { name: "l3", spine: "lp", skin: "l3", symbolPay: "lp", texture: "l3", value: null },
  8: { name: "l4", spine: "lp", skin: "l4", symbolPay: "lp", texture: "l4", value: null },
  9: { name: "l5", spine: "lp", skin: "l5", symbolPay: "lp", texture: "l5", value: null },

  // ── Hold & Win symbols ──
  10: { name: "r0", spine: null, symbolPay: "scatter", texture: null, value: null },         // Zeus placeholder — spine TBD
  11: { name: "c0", spine: "c", symbolPay: "scatter", texture: "c", value: null },           // Coin placeholder (scatter trigger)
  12: { name: "cd", spine: "cs", skin: "cd", symbolPay: "feature", texture: "cd", value: null },  // Double orb
  13: { name: "cm", spine: "cs", skin: "cm", symbolPay: "feature", texture: "cm", value: null },  // Mystery orb
  14: { name: "cc", spine: "cs", skin: "cc", symbolPay: "feature", texture: "cc", value: null },  // Collect orb
  15: { name: "c1", spine: "c", symbolPay: "coin", texture: "c", value: 1 },                // Regular coin (value from API)
  16: { name: "bl", spine: "bl", symbolPay: "blank", texture: "bl", value: null },           // Blank (static texture for bonus grid)

  // ── Jackpot coins ──
  17: { name: "c_mini", spine: "j", skin: "c_mini", symbolPay: "jackpot", texture: "c_mini", value: 15 },   // Mini (x15)
  18: { name: "c_minor", spine: "j", skin: "c_minor", symbolPay: "jackpot", texture: "c_minor", value: 50 }, // Minor (x50)
  19: { name: "c_major", spine: "j", skin: "c_major", symbolPay: "jackpot", texture: "c_major", value: 150 },// Major (x150)

  // ── Feature variants (respin only) ──
  20: { name: "cd_mult", spine: "cs", skin: "cd", symbolPay: "feature", texture: "cd", value: null },       // Double with x2 multiplier
  21: { name: "cm_minor", spine: "j", skin: "c_minor", symbolPay: "jackpot", texture: "c_minor", value: 50 },// Multi → minor
  22: { name: "cm_mini", spine: "j", skin: "c_mini", symbolPay: "jackpot", texture: "c_mini", value: 15 },  // Multi → mini
  23: { name: "cm_major", spine: "j", skin: "c_major", symbolPay: "jackpot", texture: "c_major", value: 150 },// Multi → major
  24: { name: "cd_empty", spine: "cs", skin: "cd", symbolPay: "feature", texture: null, value: null },      // BE only
  25: { name: "cc_empty", spine: "cs", skin: "cc", symbolPay: "feature", texture: null, value: null },      // BE only
};

// ─── Derived Constants ───

export const WILD_SYMBOL_ID = 0;
export const BLANK_SYMBOL_ID = 16;

// Scatter: c0 (coin placeholder) triggers Hold & Win
export const SCATTER_SYMBOL_IDS = [11];
export const SCATTER_SYMBOL_ID = 11;
export const SCATTER_1_SYMBOL_ID = 11;
export const SCATTER_2_SYMBOL_ID = null;

// Coin symbols — regular coins that display values (used during respin)
export const COIN_SYMBOL_IDS = [15]; // c1
export const ROYAL_SYMBOL_ID = null; // No royal in Zeus

// Jackpot coins — display tier via spine skins, not text
export const JACKPOT_SYMBOL_IDS = [17, 18, 19];

// Zeus orbs — feature trigger coins
export const ZEUS_DOUBLE_ID = 12;  // cd
export const ZEUS_MYSTERY_ID = 13; // cm
export const ZEUS_COLLECT_ID = 14; // cc
export const BONUS_SYMBOL_IDS = [ZEUS_DOUBLE_ID, ZEUS_MYSTERY_ID, ZEUS_COLLECT_ID];

// Respin variant IDs (post-feature reveals)
export const CD_MULT_ID = 20;     // cd_mult  — Double with x2 multiplier
export const CM_MINOR_ID = 21;    // cm_minor — Mystery → minor reveal
export const CM_MINI_ID = 22;     // cm_mini  — Mystery → mini reveal
export const CM_MAJOR_ID = 23;    // cm_major — Mystery → major reveal

// Jackpot value → symbol ID mapping (for API coin_N parsing)
export const JACKPOT_VALUE_TO_ID = {
  15: 17,   // x15 → c_mini
  50: 18,   // x50 → c_minor
  150: 19,  // x150 → c_major
};

// Raw jackpot → post-mystery reveal symbol (for BE fallback in RGS adapter)
// When mystery is active, jackpots should arrive as cm_* IDs, not c_* IDs.
export const JACKPOT_TO_MYSTERY_ID = {
  17: 22,   // c_mini  → cm_mini
  18: 21,   // c_minor → cm_minor
  19: 23,   // c_major → cm_major
};

// ─── Helper Functions ───

export const isCoinSymbol = (id) => id === 15;
// Include respin variants: 21=cm_minor, 22=cm_mini, 23=cm_major (mystery→jackpot reveals)
export const isJackpotSymbol = (id) => id === 17 || id === 18 || id === 19 || id === 21 || id === 22 || id === 23;
export const isRoyalSymbol = () => false;
// Include respin variant: 20=cd_mult (double with multiplier)
export const isBonusSymbol = (id) => id === 12 || id === 13 || id === 14 || id === 20;

export const getCoinValue = (id) => {
  const sym = symbolData[id];
  return sym?.value ?? null;
};

export const getJackpotTier = (id) => {
  if (id === 17 || id === 22) return "mini";   // c_mini + cm_mini
  if (id === 18 || id === 21) return "minor";  // c_minor + cm_minor
  if (id === 19 || id === 23) return "major";  // c_major + cm_major
  return null;
};

/**
 * Overlay z-index for the flat PotCollection container.
 * Higher value = rendered on top.
 *
 * Hierarchy:  mystery(80) > double(70) > collect(60) > major(40) > minor(30) > mini(20) > coin(10) > default(1)
 *
 * Mystery starts highest (its shuffle/transform animation must be fully visible).
 * After mystery reveal, FeatureExecutor demotes it to the revealed tier's z-index.
 */
export const getSymbolZIndex = (id) => {
  // Zeus orbs (clouds)
  if (id === ZEUS_MYSTERY_ID) return 80;
  if (id === ZEUS_DOUBLE_ID) return 70;
  if (id === ZEUS_COLLECT_ID) return 60;
  // Jackpots — by tier (includes mystery reveal variants)
  if (id === 19 || id === 23) return 40;  // major (c_major + cm_major)
  if (id === 18 || id === 21) return 30;  // minor (c_minor + cm_minor)
  if (id === 17 || id === 22) return 20;  // mini  (c_mini  + cm_mini)
  // Regular coin
  if (id === 15) return 10;
  // Everything else (blanks, etc.)
  return 1;
};

// Low-pay symbol IDs — for default slotface
export const LOW_PAY_SYMBOL_IDS = Object.entries(symbolData)
  .filter(([, d]) => d.symbolPay === "lp")
  .map(([id]) => parseInt(id));

/**
 * Default slotface — low-pay grid so no accidental wins on load.
 */
export const DEFAULT_SLOTFACE = (() => {
  const { REEL_COUNT, SYMBOLS_VISIBLE } = Settings;
  const lp = LOW_PAY_SYMBOL_IDS;
  return Array.from({ length: REEL_COUNT }, (_, ri) =>
    Array.from(
      { length: SYMBOLS_VISIBLE },
      (_, si) => lp[(ri + si) % lp.length],
    ),
  );
})();
